import type { WikiVehicle, WikiVehiclePort } from "~~/types/gamedata";

export type DistributionBucket = "weapons" | "systems" | "propulsion";

export interface DistributionSegment {
  bucket: DistributionBucket;
  label: string;
  value: number;
  percent: number;
}

export interface DistributionBar {
  key: "power" | "cooling" | "weight";
  label: string;
  unit: string;
  segments: DistributionSegment[];
}

export const DISTRIBUTION_BUCKET_LABELS: Record<DistributionBucket, string> = {
  weapons: "Waffen",
  systems: "Systeme",
  propulsion: "Antrieb",
};

// Grau/Blau/Rot wie in der FleetYards/erkul-Referenz — dieselben validierten
// Töne wie die Balkendiagramme im Performance-Tab (`BAR_COLOR_PRIMARY`/
// `BAR_COLOR_SECONDARY` in `main-tabs-performance-ships.vue`) plus ein
// neutrales Grau für die dritte Kategorie.
export const DISTRIBUTION_BUCKET_COLORS: Record<DistributionBucket, string> = {
  weapons: "#71717a",
  systems: "#009189",
  propulsion: "#c34b1c",
};

/**
 * Ordnet die rohen Item-Typ-Schlüssel aus `raw.power.used_segments_grouped`/
 * `raw.cooling.used_segments_shields_grouped` (von der API bereits über alle
 * Bauteile aggregiert) den drei Übersichts-Kategorien zu.
 */
const TYPE_BUCKET_MAP: Record<string, DistributionBucket> = {
  WeaponGun: "weapons",
  WeaponMissile: "weapons",
  WeaponDefensive: "weapons",
  MissileRack: "weapons",
  Missile: "weapons",
  Turret: "weapons",
  EMP: "weapons",
  QuantumInterdictionGenerator: "weapons",

  QuantumDrive: "propulsion",
  JumpDrive: "propulsion",
  Thruster: "propulsion",
  MainThruster: "propulsion",
  ManneuverThruster: "propulsion",

  Radar: "systems",
  Cooler: "systems",
  Shield: "systems",
  TractorBeam: "systems",
  FlightController: "systems",
  LifeSupportGenerator: "systems",
  PowerPlant: "systems",
  Scanner: "systems",
  Computer: "systems",
  GravityGenerator: "systems",
};

/** Dieselbe Zuordnung, aber über die kuratierten Baum-Kategorien (`getComponentGroup().superGroup`) — für die Gewichtsverteilung, die wir mangels ship-weiter Aggregation selbst über den Port-Baum summieren. */
const SUPERGROUP_BUCKET_MAP: Record<string, DistributionBucket> = {
  Waffen: "weapons",
  Turrets: "weapons",
  QED: "weapons",
  EMP: "weapons",
  Antrieb: "propulsion",
  Treibstoff: "propulsion",
  Triebwerk: "propulsion",
};

function emptyTotals(): Record<DistributionBucket, number> {
  return { weapons: 0, systems: 0, propulsion: 0 };
}

function sumGroupedByType(grouped: Record<string, number | null | undefined> | null | undefined): Record<DistributionBucket, number> {
  const totals = emptyTotals();
  for (const [type, value] of Object.entries(grouped ?? {})) {
    if (value == null) continue;
    totals[TYPE_BUCKET_MAP[type] ?? "systems"] += value;
  }
  return totals;
}

/**
 * Gewichtsverteilung gibt es (anders als Power/Kühlung) nicht ship-weit
 * vorgerechnet — läuft deshalb selbst rekursiv über den ganzen Port-Baum
 * (wie `collectGroupedPorts()` in `build-ship-component-groups.ts`, aber
 * bewusst eigenständig: hier soll JEDE Ausrüstung mit Masse zählen,
 * auch verschachtelte Kinder eines bereits erkannten Ports, z.B. ein
 * Jump Drive unter einem Quantenantrieb — nicht nur die oberste erkannte
 * Ebene wie im Baum für die Anzeige).
 */
function sumMassByBucket(ports: WikiVehiclePort[] | null | undefined): Record<DistributionBucket, number> {
  const totals = emptyTotals();

  function walk(list: WikiVehiclePort[]) {
    for (const port of list) {
      const equipped = port.equipped_item;
      if (equipped?.mass != null) {
        const group = getComponentGroup(port, undefined);
        if (group) totals[SUPERGROUP_BUCKET_MAP[group.superGroup] ?? "systems"] += equipped.mass;
      }
      if (port.ports?.length) walk(port.ports);
    }
  }

  walk(ports ?? []);
  return totals;
}

function toSegments(totals: Record<DistributionBucket, number>): DistributionSegment[] {
  const total = totals.weapons + totals.systems + totals.propulsion;
  return (["weapons", "systems", "propulsion"] as const).map((bucket) => ({
    bucket,
    label: DISTRIBUTION_BUCKET_LABELS[bucket],
    value: totals[bucket],
    percent: total > 0 ? (totals[bucket] / total) * 100 : 0,
  }));
}

/** Power-/Kühlungs-/Gewichtsverteilung nach Weapons/Systems/Propulsion — `null`, wenn keine der drei Quellen Daten liefert. */
export function getShipDistribution(raw: WikiVehicle | null | undefined): DistributionBar[] {
  if (!raw) return [];

  const bars: DistributionBar[] = [];

  const powerTotals = sumGroupedByType(raw.power?.used_segments_grouped);
  if (powerTotals.weapons || powerTotals.systems || powerTotals.propulsion) {
    bars.push({ key: "power", label: "Energie", unit: "", segments: toSegments(powerTotals) });
  }

  const coolingTotals = sumGroupedByType(raw.cooling?.used_segments_shields_grouped);
  if (coolingTotals.weapons || coolingTotals.systems || coolingTotals.propulsion) {
    bars.push({ key: "cooling", label: "Kühlung", unit: "", segments: toSegments(coolingTotals) });
  }

  const massTotals = sumMassByBucket(raw.ports);
  if (massTotals.weapons || massTotals.systems || massTotals.propulsion) {
    bars.push({ key: "weight", label: "Gewicht", unit: "Kg", segments: toSegments(massTotals) });
  }

  return bars;
}
